<template>
<div class="migration-wrapper">
    <div v-show="loaderVisible" class="spinner" />
    <section class="migration">
        <h1 class="migration__heading">Jira Cloud Migration</h1>
        <form class="migration-form migration__form" @submit="handleFormSubmit($event)">
            <fieldset class="migration-form__fieldset">
                <legend class="migration-form-legend migration-form__fieldset-legend">
                    <span class="migration-form-legend__title">Migration token</span>
                    <span class="migration-form-legend__subtitle"> Copy and save the token generated in Jira Cloud TFS4JIRA settings. This token will ensure a safe connection between Jira Cloud and TFS4JIRA. <a href="https://apps.appf.re/t4j/doc/sh/jira-cloud-migration/token">See Documentation</a></span>
                </legend>
                <div class="migration-form-input migration__form-fieldset-input">
                    <label class="migration-form-input__label migration-form-input__label--required" for="migration-token">Token</label>
                    <div class="migration-form-input__input-group">
                        <input
                            class="migration-form-input__input-element migration-form-input__input-element--password"
                            type="password"
                            id="migration-token"
                            :value="migrationToken"
                            :placeholder="getTokenInputPlaceholder(hasMigrationToken)"
                            @input="handleMigrationTokenChange($event)"
                            :disabled="loaderVisible"
                            :required="!hasMigrationToken"
                        />
                    </div>
                </div>
            </fieldset>
            <fieldset class="migration-form__fieldset">
                <legend class="migration-form-legend migration-form__fieldset-legend">
                    <span class="migration-form-legend__title">Jira Cloud Access</span>
                    <span class="migration-form-legend__subtitle">Define a valid Jira email and token. This information will be used when JCMA invokes TFS4JIRA app migration to update all profiles to connect to Jira cloud using this email and token. <a href="https://apps.appf.re/t4j/doc/sh/jira-cloud-migration/jira-access">See Documentation</a></span>
                </legend>
                <div class="migration-form-input migration__form-fieldset-input">
                    <label class="migration-form-input__label migration-form-input__label--required" for="migration-jira-email">Email</label>
                    <div class="migration-form-input__input-group">
                        <input
                            class="migration-form-input__input-element migration-form-input__input-element--text"
                            type="text"
                            id="migration-jira-email"
                            :value="jiraEmail"
                            @input="handleJiraEmailChange($event)"
                            :disabled="loaderVisible"
                            required
                        />
                    </div>
                </div>
                <div class="migration-form-input migration__form-fieldset-input">
                    <label class="migration-form-input__label migration-form-input__label--required" for="migration-jira-token">Token</label>
                    <div class="migration-form-input__input-group">
                        <input
                            class="migration-form-input__input-element migration-form-input__input-element--password"
                            type="password" id="migration-jira-token"
                            :value="jiraToken"
                            :placeholder="getTokenInputPlaceholder(hasJiraToken)"
                            @input="handleJiraTokenChange($event)"
                            :disabled="loaderVisible"
                            :required="!hasJiraToken"
                        />
                        <a class="migration-form-input__input-link" href="https://apps.appf.re/t4j/doc/sh/jira-cloud-migration/jira-token">How do I generate a token?</a>
                    </div>
                </div>
            </fieldset>
            <fieldset class="migration-form__fieldset">
                <div class="migration-form-input migration__form-fieldset-input migration-form-input--flipped">
                    <div class="migration-form-input__input-group">
                        <input
                            class="migration-form-input__input-element migration-form-input__input-element--checkbox"
                            type="checkbox"
                            id="migration-enable-new-profiles"
                            @change="handleEnableMigratedProfilesToggle($event)"
                            :disabled="loaderVisible"
                            :checked="enableMigratedProfiles"
                        />
                    </div>
                    <label class="migration-form-input__label" for="migration-enable-new-profiles">Enable new profile after successful migration (disables the original one)</label>
                </div>
            </fieldset>
            <fieldset class="migration-form__fieldset migration-form__fieldset--with-top-separator migration-form__fieldset--with-bottom-margin">
                <div class="migration-form-input">
                    <span class="migration-form-input__label" aria-hidden="true"></span>
                    <div class="migration-form-input__input-group migration-form-input__input-group--inline">
                        <input
                            class="migration-form-input__input-element migration-form-input__input-element--submit"
                            id="migration-save-settings"
                            type="submit"
                            value="Save"
                            :disabled="!isSaveButtonEnabled"
                        />
                        <input
                            class="migration-form-input__input-element migration-form-input__input-element--button"
                            id="migration-test-connection"
                            type="button"
                            value="Test"
                            @click="handleConnectionTest"
                        />
                    </div>
                </div>
            </fieldset>
        </form>
    </section>
</div>
</template>
<script>
import {mapActions, mapMutations, mapGetters} from 'vuex';
import MigrationService from '../service/migration.js';
import { NAMESPACE as APP_NAMESPACE } from '../store/modules/app.js';
import axios from 'axios';

export default {
    props: ['loadOnInit'],
    data() {
        return {
            savedMigrationSettings: this.currentSettings,
        };
    },
    computed: {
        ...mapGetters('migration', [
            'loaderVisible',
            'jiraEmail',
            'jiraToken',
            'hasJiraToken',
            'migrationToken',
            'hasMigrationToken',
            'enableMigratedProfiles',
        ]),
        currentSettings() {
            return {
                jiraEmail: this.jiraEmail,
                jiraToken: this.jiraToken,
                migrationToken: this.migrationToken,
                enableMigratedProfiles: this.enableMigratedProfiles,
            };
        },
        isSaveButtonEnabled() {
            if (this.savedMigrationSettings === undefined || this.loaderVisible) {
                return false;
            }

            return (
                this.jiraEmail !== this.savedMigrationSettings.jiraEmail
                || this.jiraToken !== this.savedMigrationSettings.jiraToken
                || this.migrationToken !== this.savedMigrationSettings.migrationToken
                || this.enableMigratedProfiles !== this.savedMigrationSettings.enableMigratedProfiles
            );
        },
    },
    mounted: function () {
        if (this.loadOnInit === 'true') {
            this.initComponent();
        }
    },
    methods: {
        ...mapActions('migration', ['setService', 'fetchMigrationSettings', 'saveMigrationSettings', 'testJiraConnection']),
        ...mapMutations('migration', ['setJiraEmail', 'setJiraToken', 'setMigrationToken', 'setEnableMigratedProfiles']),
        ...mapGetters({
            contextPath: APP_NAMESPACE.getters.CONTEXT_PATH,
        }),
        initComponent: async function () {
            this.setService({
                service: new MigrationService(axios, this.contextPath()),
            });

            await this.fetchMigrationSettings();
            this.savedMigrationSettings = this.currentSettings;

            this.loadOnInit = 'true';
        },
        getTokenInputPlaceholder(hasToken) {
            return hasToken
                ? 'Edit to change'
                : '';
        },
        async handleFormSubmit(event) {
            event.preventDefault();

            const success = await this.saveMigrationSettings(this.currentSettings);
            if (success) {
                this.savedMigrationSettings = this.currentSettings;
            }
        },
        handleJiraEmailChange(event) {
            this.setJiraEmail(event.target.value);
        },
        handleJiraTokenChange(event) {
            this.setJiraToken(event.target.value);
        },
        handleMigrationTokenChange(event) {
            this.setMigrationToken(event.target.value);
        },
        handleEnableMigratedProfilesToggle(event) {
            this.setEnableMigratedProfiles(event.target.checked);
        },
        async handleConnectionTest() {
            await this.testJiraConnection(this.currentSettings);
        },
    },
}
</script>
<style scoped>

.migration-wrapper {
    display: flex;
    justify-content: center;
    width: 1000px;
}

.migration {
    width: 90%;
}

#main h1.migration__heading {
    margin-bottom: 1em;
    border-bottom: 1px solid #d3d3d3;
    padding-bottom: 0.3em;
}

.migration-form {
    display: flex;
    flex-direction: column;
    row-gap: 35px;
}

.migration-form__fieldset {
    display: flex;
    flex-direction: column;
    row-gap: 0.75em;
    margin: 0;
    padding: 0;
    border: 0;
}

.migration-form__fieldset--with-top-separator::before {
    content: "";
    margin: -11px 0 0.75em;
    border-top: 1px solid #ccc;
}

.migration-form__fieldset--with-bottom-margin {
    margin-bottom: 3em;
}

.migration-form-legend {
    display: flex;
    flex-direction: column;
    row-gap: 0.35em;
    margin-bottom: 20px;
    padding: 0;
}

.migration-form-legend__title {
    font-size: 16px;
    font-weight: bold;
    color: #000;
}

.migration-form-legend__subtitle {
    line-height: 18px;
}

.migration-form-input {
    display: flex;
    column-gap: 5px;
}

.migration-form-input__input-group {
    display: flex;
    row-gap: 0.5em;
    flex: 12;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
}

.migration-form-input__input-group--inline {
    flex-direction: row;
    justify-content: flex-start;
    column-gap: 1em;
}

input[type=checkbox].migration-form-input__input-element--checkbox {
    align-self: flex-end;
    margin: 0;
}

input[type=submit].migration-form-input__input-element--submit,
input[type=button].migration-form-input__input-element--button {
    width: 6em;
    padding: 0.1em 0.2em;
    font-size: 1.2em;
}

.migration-form-input__label {
    display: flex;
    flex: 1;
    height: 24px;
    align-items: center;
    justify-content: right;
}

.migration-form-input__label--required::after {
    content: "*";
    color: red;
    padding-left: 4px;
}

.migration-form-input--flipped > .migration-form-input__input-group {
    flex: 0;
}

.migration-form-input--flipped > .migration-form-input__label {
    flex: 12;
    justify-content: left;
}
</style>



// WEBPACK FOOTER //
// src/components/MigrationSettings.vue